import apiClient, { handleApiResponse } from './client';
import type {
  Subscription,
  UsageStats,
  LimitCheckResponse,
  SubscriptionTier,
  SubscriptionTierInfo,
  BillingHistoryItem,
} from '@/types/subscription';
import type { ApiResponse } from '@/types';
import { AxiosError } from 'axios';

// Base URL relative to API client's /api prefix
const BASE_URL = '/v1/subscriptions';

/**
 * Default subscription for users without an active subscription
 */
const DEFAULT_SUBSCRIPTION: Subscription = {
  id: 0,
  user_id: 0,
  tier: 'FREE',
  status: 'EXPIRED', // No active subscription
  employee_limit: 0,
  billing_period: 'MONTHLY',
  price: 0,
  api_key: '',
  started_at: new Date().toISOString(),
  expires_at: null,
  features: [],
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

/**
 * Default usage stats for users without usage history
 */
const DEFAULT_USAGE_STATS: UsageStats = {
  current_period: {
    employees_processed: 0,
    submissions_count: 0,
    employee_limit: 0,
    period_start: new Date().toISOString(),
    period_end: new Date().toISOString(),
  },
  history: [],
};

export const subscriptionApi = {
  /**
   * Get current user's subscription
   * Returns default FREE subscription if user has none (404)
   */
  getCurrentSubscription: async (): Promise<Subscription> => {
    try {
      const response = await apiClient.get<ApiResponse<Subscription>>(
        `${BASE_URL}/current`
      );
      return handleApiResponse(response);
    } catch (error) {
      // Return default subscription for 404 (no subscription yet)
      if (error instanceof AxiosError && error.response?.status === 404) {
        return DEFAULT_SUBSCRIPTION;
      }
      throw error;
    }
  },

  /**
   * Get usage statistics for current billing period
   * Returns empty usage stats if user has none (404)
   */
  getUsageStats: async (): Promise<UsageStats> => {
    try {
      const response = await apiClient.get<ApiResponse<UsageStats>>(
        `${BASE_URL}/usage`
      );
      return handleApiResponse(response);
    } catch (error) {
      // Return default usage stats for 404 (no usage history yet)
      if (error instanceof AxiosError && error.response?.status === 404) {
        return DEFAULT_USAGE_STATS;
      }
      throw error;
    }
  },

  /**
   * Check if user can add more employees to their submission
   *
   * CORRECTED: No submission_id param (backend doesn't accept it)
   *
   * @param currentCount - Current employees in the draft submission
   * @param additionalCount - How many more employees to add (default: 1)
   */
  checkLimit: async (
    currentCount: number = 0,
    additionalCount: number = 1
  ): Promise<LimitCheckResponse> => {
    const response = await apiClient.get<ApiResponse<LimitCheckResponse>>(
      `${BASE_URL}/check-limit`,
      {
        params: {
          current_count: currentCount,
          additional_count: additionalCount,
        },
      }
    );
    return handleApiResponse(response);
  },

  /**
   * Check if bulk add is allowed (convenience wrapper)
   *
   * @param currentCount - Current employees in submission
   * @param selectedCount - Number of profiles being added
   */
  checkBulkLimit: async (
    currentCount: number,
    selectedCount: number
  ): Promise<LimitCheckResponse> => {
    return subscriptionApi.checkLimit(currentCount, selectedCount);
  },

  /**
   * Get all available subscription tiers
   * THIS IS THE SOURCE OF TRUTH for pricing and features
   */
  getTiers: async (): Promise<SubscriptionTierInfo[]> => {
    const response = await apiClient.get<ApiResponse<SubscriptionTierInfo[]>>(
      `${BASE_URL}/tiers`
    );
    return handleApiResponse(response);
  },

  /**
   * Upgrade subscription to a new tier
   */
  upgradeSubscription: async (
    tier: SubscriptionTier
  ): Promise<Subscription> => {
    const response = await apiClient.post<ApiResponse<Subscription>>(
      `${BASE_URL}/upgrade`,
      { tier }
    );
    return handleApiResponse(response);
  },

  /**
   * Cancel subscription (effective at period end)
   */
  cancelSubscription: async (): Promise<{
    status: string;
    expires_at: string;
  }> => {
    const response = await apiClient.post<
      ApiResponse<{ status: string; expires_at: string }>
    >(`${BASE_URL}/cancel`);
    return handleApiResponse(response);
  },

  /**
   * Regenerate API key
   */
  regenerateApiKey: async (): Promise<{ api_key: string }> => {
    const response = await apiClient.post<ApiResponse<{ api_key: string }>>(
      `${BASE_URL}/api-key/regenerate`
    );
    return handleApiResponse(response);
  },

  /**
   * Get billing history
   */
  getBillingHistory: async (): Promise<BillingHistoryItem[]> => {
    const response = await apiClient.get<ApiResponse<BillingHistoryItem[]>>(
      `${BASE_URL}/invoices`
    );
    return handleApiResponse(response);
  },
};

export default subscriptionApi;
